import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Grid2,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Container,
} from "@mui/material";
import ClientActivity from "./ClientActivity";
import ReviewClientActivity from "./ReviewClientActivity";
import { useApi } from "../../utils/api";
import Loading from "../Layout/Loading";
import MenuIcon from "@mui/icons-material/Menu";

const ReviewClients = () => {
  const [getReviewedActivity, setGetReviewedActivity] =
    useState("not-reviewed");
  const [allActivity, setAllActivity] = useState(null);
  const [reviewingActivity, setReviewingActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [days, setDays] = useState(30);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { getActivity, getClients } = useApi();
  const debounceTimeoutRef = useRef(null);
  const activityAbortControllerRef = useRef(null);

  useEffect(() => {
    loadClients();
  }, []);

  const debouncedLoadActivity = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      loadActivity();
    }, 300); // 300ms debounce delay
  }, [selectedClient, days, getReviewedActivity]);

  useEffect(() => {
    debouncedLoadActivity();

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [debouncedLoadActivity]);

  useEffect(() => {
    // Abort any in-flight requests when unmounting
    return () => {
      if (activityAbortControllerRef.current) {
        activityAbortControllerRef.current.abort();
      }
    };
  }, []);

  const loadClients = async () => {
    const clientsData = await getClients();
    setClients(clientsData);
  };

  const onClick = (activity) => {
    setReviewingActivity(activity);
  };

  const loadActivity = async () => {
    // Cancel any in-flight activity request before starting a new one
    if (activityAbortControllerRef.current) {
      activityAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    activityAbortControllerRef.current = controller;

    setLoading(true);
    try {
      const activityData = await getActivity(
        getReviewedActivity === "reviewed" ? 1 : 0,
        selectedClient,
        days,
        controller.signal
      );

      // Only update state if this is still the latest request
      if (activityAbortControllerRef.current === controller) {
        setAllActivity(activityData);
        setReviewingActivity(activityData[0]);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error loading activity:", error);
      }
    } finally {
      if (
        activityAbortControllerRef.current === controller &&
        !controller.signal.aborted
      ) {
        setLoading(false);
      }
    }
  };

  const handleWorkoutReviewed = (reviewedActivityId) => {
    // Remove the reviewed workout from the list without refetching
    if (allActivity) {
      const filteredActivity = allActivity.filter(
        (activity) => activity.id !== reviewedActivityId
      );
      setAllActivity(filteredActivity);

      // Select the next activity, or null if list is empty
      if (filteredActivity.length > 0) {
        setReviewingActivity(filteredActivity[0]);
      } else {
        setReviewingActivity(null);
      }
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Grid2 item xs={12}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <div>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ mr: 2 }}>
              Client Activity {allActivity && `(${allActivity.length})`}
            </Typography>
          </div>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Select Client</InputLabel>
              <Select
                value={selectedClient}
                label="Select Client"
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <MenuItem value="">All Clients</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="Days Ago"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 30)}
              size="small"
              sx={{ width: 120 }}
              inputProps={{ min: 1, max: 365 }}
            />
            <ToggleButtonGroup
              color="primary"
              value={getReviewedActivity}
              exclusive
              onChange={() =>
                setGetReviewedActivity(
                  getReviewedActivity === "reviewed"
                    ? "not-reviewed"
                    : "reviewed"
                )
              }
              size="small"
            >
              <ToggleButton value="not-reviewed" size="small">
                Not reviewed
              </ToggleButton>
              <ToggleButton value="reviewed" size="small">
                Reviewed
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Grid2>
      {loading ? (
        <Loading />
      ) : (
        <Box sx={{ display: "flex" }}>
          <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? mobileOpen : true}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              width: { xs: 300 },
              flexShrink: 0,
              overflow: "visible",
              "& .MuiDrawer-paper": {
                width: { xs: 300 },
                boxSizing: "border-box",
                position: isMobile ? "fixed" : "relative",
                height: isMobile ? "100%" : "auto",
                borderRight: "none",
                overflow: "visible",
              },
            }}
          >
            <Box>
              <ClientActivity
                allActivity={allActivity}
                selectedActivity={reviewingActivity}
                onClick={(activity) => {
                  onClick(activity);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
              />
            </Box>
          </Drawer>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              px: 3,
              py: 2,
            }}
          >
            {reviewingActivity && (
              <ReviewClientActivity
                activityLoading={loading}
                reviewingActivity={reviewingActivity}
                onReviewed={loadActivity}
                onWorkoutReviewed={handleWorkoutReviewed}
              />
            )}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default ReviewClients;
